import { get as httpGet, put as httpPut } from '@/services/http';

/**
 * US-092 — merchant-wide receive preferences that are not column visibility.
 *
 * The "Update supplier cost" toggle lives in the existing
 * `po_landed_cost_preferences` UserConfigs record, next to the landed-cost
 * method: both are PO cost behaviour, and reusing the record avoids a second
 * config key and endpoint pair.
 *
 * Merchant-wide rather than `localStorage` (which its neighbouring
 * `po_receive_cost_method` still uses) because this one gates a data mutation,
 * not a display choice — one buyer switching it off locally while a teammate's
 * browser leaves it on would rewrite supplier prices inconsistently.
 */

const ENDPOINT = '/purchase_orders/config/landed-cost';

interface GetDep { get: (url: string, params?: any) => Promise<any>; }
interface PutDep { put: (url: string, body?: any) => Promise<any>; }

/** What the dialog knows about the toggle after trying to load it. */
export interface SupplierCostPreference {
  /** What to show on the switch. */
  value: boolean;
  /**
   * False when the read failed. The dialog then OMITS `updateSupplierCost` from
   * the receive payload so the server resolves it from stored state, rather
   * than asserting a value we could not read.
   */
  loaded: boolean;
}

/**
 * The toggle is OPT-OUT, so an absent preference, a malformed payload or a
 * non-boolean all resolve to `true` — reading those as "off" would silently
 * stop keeping SupplierProduct.cost current.
 *
 * A failed REQUEST is a different state and must not be collapsed into that
 * default: a merchant who explicitly set the preference to `false` would
 * otherwise have supplier costs rewritten for every received variant after one
 * transient network error. It reports `loaded: false` instead, and the caller
 * declines to assert a value.
 */
export async function fetchUpdateSupplierCostPreference(
  deps: GetDep = { get: httpGet },
): Promise<SupplierCostPreference> {
  try {
    const response: any = await deps.get(ENDPOINT);
    const stored = response?.data?.updateSupplierCostOnReceive;
    return { value: typeof stored === 'boolean' ? stored : true, loaded: true };
  } catch {
    return { value: true, loaded: false };
  }
}

/**
 * Sends only this key. The endpoint merges against what is stored, so a partial
 * write leaves the merchant's landed-cost method untouched.
 */
export async function saveUpdateSupplierCostPreference(
  updateSupplierCostOnReceive: boolean,
  deps: PutDep = { put: httpPut },
): Promise<void> {
  await deps.put(ENDPOINT, { updateSupplierCostOnReceive });
}
